import { requestGQL } from 'common/graphQlClient';
import { useQueryClient } from 'react-query';

import { Option } from '@mobily/ts-belt';
import { allPremisesKey } from 'app/premises/hooks/usePremisesList';
import { GetPropertyConnectionResponse, LinkData } from 'app/connection/api';
import { generateNodeId } from 'common/offline/generateNodeId';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';
import { propertyConnectionKey } from 'app/areas/hooks/useGetPropertyConnections';
import { CREATE_TWISTER_PAIR } from '../outlets/api';
import { usePropertyContext } from '../properties/components/SingleProperty';

// Full type for twisted pair creation (the exported type in api.ts is incomplete)
type TwistedPairInput = {
  id: string | null;
  from: { portId: string; nodeId: string };
  to: { portId: string; nodeId: string };
  length: number;
  category: string;
  shielding: string;
};

// Type with generated ID (for offline support)
type TwistedPairWithId = Omit<TwistedPairInput, 'id'> & { id: string };

export const useCreateTwistedPair = (id?: Option<number>) => {
  const queryClient = useQueryClient();
  const { id: contextPropertyId } = usePropertyContext();

  const propertyId = id || contextPropertyId || null;
  const cacheKey = propertyConnectionKey(propertyId);

  const { data, mutate, mutateAsync, isLoading, isError, isSuccess } = useOfflineMutation<
    string,
    unknown,
    TwistedPairInput
  >({
    mutationFn: async (twistedPair) =>
      requestGQL(CREATE_TWISTER_PAIR, {
        propertyId,
        twistedPair,
      }),
    mutationName: 'createTwistedPair',
    entity: 'twistedPair',
    operationType: 'CREATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [['propertyConnection', propertyId], allPremisesKey(propertyId)],

    // Prepare payload for offline queue - ensure it has an ID
    getOptimisticData: (twistedPair) => {
      const twistedPairWithId: TwistedPairWithId = {
        ...twistedPair,
        id: twistedPair.id ?? generateNodeId(),
      };
      return twistedPairWithId;
    },

    // Apply optimistic update to connection cache - add link to linkDataArray
    applyOptimisticUpdate: (optimisticData) => {
      const twistedPair = optimisticData as TwistedPairWithId;

      queryClient.setQueryData<GetPropertyConnectionResponse | undefined>(cacheKey, (oldData) => {
        if (!oldData?.propertyById?.connectionByConnectionId?.serialized2) {
          return oldData;
        }

        const existingLinks = oldData.propertyById.connectionByConnectionId.serialized2.linkDataArray ?? [];

        // Create optimistic link data matching LinkData type
        const newLink: LinkData = {
          id: twistedPair.id,
          from: twistedPair.from.nodeId,
          to: twistedPair.to.nodeId,
          fromPort: twistedPair.from.portId,
          toPort: twistedPair.to.portId,
          text: null,
          highlight: false,
          type: 'TwistedPair',
          cfObject: {
            length: twistedPair.length,
            category: twistedPair.category,
            shielding: twistedPair.shielding,
          },
        };

        return {
          ...oldData,
          propertyById: {
            ...oldData.propertyById,
            connectionByConnectionId: {
              ...oldData.propertyById.connectionByConnectionId,
              serialized2: {
                ...oldData.propertyById.connectionByConnectionId.serialized2,
                linkDataArray: [...existingLinks, newLink],
              },
            },
          },
        };
      });
    },
  });

  return {
    data,
    mutate: (input: TwistedPairInput, opts?: any) =>
      mutate(
        {
          ...input,
          id: input.id ?? generateNodeId(),
        },
        opts,
      ),
    mutateAsync: (input: TwistedPairInput, opts?: any) =>
      mutateAsync(
        {
          ...input,
          id: input.id ?? generateNodeId(),
        },
        opts,
      ),
    isLoading,
    isError,
    isSuccess,
  };
};
